import { Drawer, Skeleton } from "antd";
import React, { useCallback, useRef, useState } from "react";
import { setDJFormVisibility } from "@/store/djMessageForm/action";
import { RootState } from "@/store/store";
import { connect, ConnectedProps } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
require("@/styles/djMessageForm.less");

import { useTimeout } from "@/utils/hooks";
import DJMessageForm from "./DJMessageForm";

const mapState = (state: RootState) => ({
  visible: state.djMessageForm.visible,
});

const mapDispatch = {
  setDJFormVisibility,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & {
  welcomeMessage: string;
  thankYouMessage: string;
};

const DJMessageDrawer = ({
  visible,
  setDJFormVisibility,
  welcomeMessage,
  thankYouMessage,
}: Props) => {
  const [fxStage, setFxStage] = useState(0);

  const endElementRef = useRef<HTMLLIElement>(null);

  const [userMessages, setUserMessages] = useState([]);

  useTimeout(() => {
    setFxStage(1);
  }, 1000);

  const onSentMessage = useCallback((msg) => {
    // append message
    setUserMessages((u) => [...u, msg]);

    // scroll to bottom message
    if (endElementRef != null && endElementRef.current != null) {
      endElementRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <Drawer
      title="Message the DJ..."
      width={720}
      onClose={() => setDJFormVisibility(false)}
      visible={visible}
      bodyStyle={{ paddingBottom: 80 }}
      className={"dj-message-form"}
      /**
       * @todo split into two components properly!
       */
      footer={<DJMessageForm onSentMessage={onSentMessage} />}
    >
      {fxStage === 0 ? (
        <>
          <Skeleton active avatar paragraph={{ rows: 4 }} />
          <Skeleton active avatar paragraph={{ rows: 4 }} />
          <Skeleton active avatar paragraph={{ rows: 4 }} />{" "}
        </>
      ) : null}
      {fxStage === 1 ? (
        <>
          <ul className="messages">
            <AnimatePresence initial={true}>
              {welcomeMessage.trim().length !== 0 &&
                welcomeMessage.split("\n").map((text: string, i: number) => (
                  <motion.li
                    key={text}
                    initial={{
                      opacity: 0,
                      y: 50,
                      scale: 0.3,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1,
                      transition: { delay: 1 * i },
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.5,
                      transition: { duration: 0.2 },
                    }}
                  >
                    {text}
                  </motion.li>
                ))}
              {userMessages.length !== 0
                ? userMessages.map((msg, i) => (
                    <motion.li
                      key={"user-msg-" + i}
                      initial={{ opacity: 0, y: 50, scale: 0.3 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      className="right"
                      exit={{
                        opacity: 0,
                        scale: 0.5,
                        transition: { duration: 0.2 },
                      }}
                    >
                      {msg}
                    </motion.li>
                  ))
                : null}
              {userMessages.length !== 0 &&
                thankYouMessage.trim().length !== 0 &&
                thankYouMessage.split("\n").map((id, i) => (
                  <motion.li
                    key={id}
                    ref={endElementRef}
                    initial={{
                      opacity: 0,
                      y: 50,
                      scale: 0.3,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1,
                      transition: { delay: 1 },
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.5,
                      transition: { duration: 0.2 },
                    }}
                  >
                    {id}
                  </motion.li>
                ))}
            </AnimatePresence>
          </ul>
        </>
      ) : null}
    </Drawer>
  );
};

export default connector(DJMessageDrawer);
